/* eslint-disable no-console */
import isMobile from "ismobilejs";
import { useEffect, useRef } from "react";

const useSticky = (wrapperId: string, buttonIds: string[], offset: number, top: number) => {
    const stickyOffsetsRef = useRef<number[]>([]);
    const stickyWidthsRef = useRef<number[]>([]);

    useEffect(() => {
        const stickyWrappers = document.querySelectorAll(`#${wrapperId}`);
        const footer = document.getElementsByTagName("footer")[0];
        const backToTop = document.getElementById("back-to-top");

        if (stickyWrappers.length === 0) {
            return;
        }

        if (!footer && !backToTop) {
            return useStickyWithNoFooter(stickyWrappers, buttonIds, offset, top);
        }

        if (!backToTop) {
            return;
        }

        const buttons = buttonIds.map((selector: string) => {
            if (selector.startsWith("[")) {
                return document.querySelector(selector) as HTMLElement;
            }
            return document.getElementById(selector);
        });

        // Store the initial sticky offsets and widths only once
        if (stickyOffsetsRef.current.length === 0) {
            stickyOffsetsRef.current = Array.from(stickyWrappers).map(wrapper => {
                const element = wrapper;
                return element.getBoundingClientRect().top + window.scrollY;
            });
        }

        const stickyWidths = Array.from(stickyWrappers).map(wrapper => {
            const element = wrapper as HTMLElement;
            return element.offsetWidth;
        });

        const showMobile = isMobile().any;

        if (!showMobile) {
            const handleScroll = () => {
                const footerOffset = footer.getBoundingClientRect().top + window.scrollY;
                const backToTopHeight = backToTop.getBoundingClientRect().height;
                const backToTopTop = backToTop.getBoundingClientRect().top + window.scrollY;
                const backToTopBottom = backToTopTop + backToTopHeight;

                Array.from(stickyWrappers).forEach((wrapper, index) => {
                    const element = wrapper as HTMLElement;
                    const stickyHeight = element.offsetHeight;
                    const stickyOffset = stickyOffsetsRef.current[index];
                    const stickyWidth = stickyWidths[index];
                    const maxStickyTop = Math.min(footerOffset, backToTopBottom) - stickyHeight - offset; // Stop before the footer or left div bottom

                    if (window.scrollY > stickyOffset - offset && window.scrollY < maxStickyTop) {
                        console.log("stickyOffset inside:", stickyOffset);
                        element.style.position = "fixed";
                        element.style.top = `${top}px`;
                        element.style.zIndex = "1000";
                        element.style.width = `${stickyWidth}px`;
                        element.style.height = `${stickyHeight}px`;
                    } else if (window.scrollY >= maxStickyTop) {
                        element.style.position = "absolute";
                        element.style.top = `${maxStickyTop - stickyOffset + offset}px`;
                    } else {
                        element.style.position = "static";
                        element.style.top = "";
                        element.style.width = "";
                        element.style.height = "";

                        buttons.forEach(button => {
                            if (button) {
                                button.style.position = "static";
                                button.style.top = "";
                                button.style.width = "";
                            }
                        });
                    }
                });
            };

            window.addEventListener("scroll", handleScroll);

            handleScroll();
            // Cleanup the event listener on component unmount
            return () => {
                window.removeEventListener("scroll", handleScroll);
            };
        }
    }, [wrapperId, buttonIds, offset, top]);
};

const useStickyWithNoFooter = (
    stickyWrappers: NodeListOf<Element>,
    buttonIds: string[],
    offset: number,
    top: number,
) => {
    const buttons = buttonIds.map((selector: string) => {
        if (selector.startsWith("[")) {
            return document.querySelector(selector) as HTMLElement;
        }
        return document.getElementById(selector);
    });
    // Calculate the distance from the top of the page to the element
    const stickyOffsets = Array.from(stickyWrappers).map(wrapper => {
        const element = wrapper as HTMLElement;
        return element.getBoundingClientRect().top + window.scrollY;
    });

    const stickyWidths = Array.from(stickyWrappers).map(wrapper => {
        const element = wrapper as HTMLElement;
        return element.offsetWidth;
    });

    const showMobile = isMobile().any;
    if (!showMobile) {
        const handleScroll = () => {
            Array.from(stickyWrappers).forEach((wrapper, index) => {
                const element = wrapper as HTMLElement;
                const stickyHeight = element.offsetHeight;
                const stickyOffset = stickyOffsets[index];
                const stickyWidth = stickyWidths[index];

                if (window.scrollY > stickyOffset - offset) {
                    element.style.position = "fixed";
                    element.style.top = `${top}px`;
                    element.style.zIndex = "1000";
                    element.style.width = `${stickyWidth}px`;
                    element.style.height = `${stickyHeight}px`;
                } else {
                    element.style.position = "static";
                    element.style.top = "";
                    element.style.width = "";
                    element.style.height = "";

                    buttons.forEach(button => {
                        if (button) {
                            button.style.position = "static";
                            button.style.top = "";
                            button.style.width = "";
                        }
                    });
                }
            });
        };

        window.addEventListener("scroll", handleScroll);

        // Cleanup the event listener on component unmount
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }
};

export default useSticky;
